import type { CSSProperties, MouseEvent, ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPlay } from "@fortawesome/free-solid-svg-icons";
import type { TeacherLessonRecord } from "../auth/api-client";
import { fallbackGradient, lessonIconDefinition } from "./lessonIcons";
import { isAdvancedLessonMode, lessonCardEpicWrapStyle, lessonEpicVisualStyle } from "./lessonAdvancedVisual";
import { textColorsForBackground } from "./lessonCardColors";
import { teacherPreviewLessonUrl } from "./teacherPreviewLessonUrl";
import { useLessonLaunch } from "../transition/LessonLaunchProvider";
import {
  LessonLinkedStandardsBadge,
  type LinkedStandardSummary,
} from "./LessonLinkedStandardsBadge";

export interface LessonLibraryCardProps {
  readonly lesson: TeacherLessonRecord;
  /**
   * Non-interactive read-only rendering for public showcases: no navigation,
   * no edit/delete toolbar. Safe to mount outside the teacher app.
   */
  readonly showcase?: boolean;
  /** When set, shows a hover tooltip listing linked standard codes (demo/marketing). */
  readonly linkedStandards?: readonly LinkedStandardSummary[];
  /** Status pills rendered as an overlay in the top-left corner of the card. */
  readonly overlay?: ReactNode;
  /** Extra controls appended to the card toolbar (e.g. a per-lesson actions menu). */
  readonly extraTools?: ReactNode;
  /** Called when the pointer leaves the card (used to dismiss open menus). */
  readonly onPointerLeave?: () => void;
}

export function LessonLibraryCard({
  lesson,
  showcase = false,
  linkedStandards,
  overlay,
  extraTools,
  onPointerLeave,
}: LessonLibraryCardProps) {
  const launch = useLessonLaunch();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;
  const epic = lessonEpicVisualStyle({
    workspaceMode: lesson.workspaceMode,
    baseColor: lesson.iconBgColor,
    fallbackSeed: lesson.id,
  });
  const background = epic?.innerBackground ?? lesson.iconBgColor?.trim() ?? fallbackGradient(lesson.id);
  const colors = textColorsForBackground(background);
  const watermarkColor = lesson.iconFgColor?.trim() || colors.title;
  const icon = lessonIconDefinition(lesson.iconName);
  const initial = lesson.title.trim().charAt(0).toUpperCase() || "?";
  const advanced = isAdvancedLessonMode(lesson.workspaceMode);
  const previewUrl = teacherPreviewLessonUrl({
    lessonId: lesson.id,
    lessonTitle: lesson.title,
    iconName: lesson.iconName,
    iconBgColor: lesson.iconBgColor,
    iconFgColor: lesson.iconFgColor,
    workspaceMode: lesson.workspaceMode,
    returnTo,
  });
  const editUrl = `/teacher/lessons/${lesson.id}`;
  const epicWrap = epic ? lessonCardEpicWrapStyle(epic) : null;

  const onPlay = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    launch({ to: previewUrl, background, watermarkColor, iconDef: icon });
  };

  const cardInner = (
    <>
      <span className="teacher-lesson-card-watermark" style={{ color: watermarkColor }} aria-hidden>
        {icon ? <FontAwesomeIcon icon={icon} fixedWidth /> : <span>{initial}</span>}
      </span>
      <div className="teacher-lesson-card-body">
        <h3 className="teacher-lesson-card-title" style={{ color: colors.title }}>
          {lesson.title}
        </h3>
        <p className="teacher-lesson-card-blurb" style={{ color: colors.blurb }}>
          {lesson.description || "Preview this lesson as a student."}
        </p>
        {linkedStandards && linkedStandards.length > 0 ? (
          <div className="teacher-lesson-card-standards">
            <LessonLinkedStandardsBadge standards={linkedStandards} variant="on-card" />
          </div>
        ) : (lesson.standardsCount ?? 0) > 0 || (lesson.glossaryCount ?? 0) > 0 ? (
          <p className="teacher-lesson-card-standards" style={{ color: colors.blurb }}>
            {(lesson.standardsCount ?? 0) > 0 ? `${lesson.standardsCount} standard${lesson.standardsCount === 1 ? "" : "s"}` : null}
            {(lesson.standardsCount ?? 0) > 0 && (lesson.glossaryCount ?? 0) > 0 ? " · " : null}
            {(lesson.glossaryCount ?? 0) > 0 ? `${lesson.glossaryCount} term${lesson.glossaryCount === 1 ? "" : "s"}` : null}
          </p>
        ) : null}
      </div>
      {showcase ? null : (
        <span className="teacher-lesson-card-play-badge" aria-hidden>
          <FontAwesomeIcon icon={faPlay} />
        </span>
      )}
    </>
  );

  return (
    <article
      className={["teacher-lesson-card-wrap", epicWrap?.className ?? ""].filter(Boolean).join(" ")}
      data-tone={colors.tone}
      data-workspace={advanced ? "advanced" : "simple"}
      style={(epicWrap?.style ?? { "--lesson-card-bg": background }) as CSSProperties}
      title={advanced ? `${lesson.title} · Advanced multi-file workspace` : undefined}
      onMouseLeave={onPointerLeave}
    >
      <div className="teacher-lesson-card">
        {overlay ? <div className="teacher-lesson-card-overlay">{overlay}</div> : null}
        {showcase ? (
          <div className="teacher-lesson-card-play" aria-hidden>
            {cardInner}
          </div>
        ) : (
          <Link
            to={previewUrl}
            onClick={onPlay}
            className="teacher-lesson-card-play"
            aria-label={`Preview ${lesson.title}${advanced ? " (advanced workspace)" : ""}`}
          >
            {cardInner}
          </Link>
        )}
        {showcase ? null : (
          <div className="teacher-lesson-card-toolbar">
            <Link
              to={editUrl}
              className="teacher-lesson-card-tool"
              title="Edit lesson"
              aria-label={`Edit ${lesson.title}`}
            >
              <FontAwesomeIcon icon={faPen} fixedWidth />
            </Link>
            {extraTools}
          </div>
        )}
      </div>
    </article>
  );
}
